/* eslint-disable unicorn/no-null */
import { StateCreator } from 'zustand';

export enum OTAStatus {
  IDLE = 'IDLE',
  DOWNLOADING = 'DOWNLOADING',
  VERIFYING = 'VERIFYING',
  EXTRACTING = 'EXTRACTING',
  INSTALLING = 'INSTALLING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  PAUSED = 'PAUSED',
  CANCELLED = 'CANCELLED',
}

export interface OTAUpdateItem {
  id: string;
  version: number;
  size: number;
  createdAt: string;
  sha256: string;
}

export interface OTAProgress {
  status: OTAStatus;
  updateId: string;
  updateName: string;
  totalBytes: number;
  downloadedBytes: number;
  extractedBytes?: number;
  percentage: number;
  speed: number; // bytes per second
  eta: number; // seconds
  error?: string;
  startTime?: number;
  diskSpace?: {
    hasSpace: boolean;
    availableBytes: number;
    requiredBytes: number;
    needsToFreeBytes?: number;
  };
}

export interface OTASlice {
  availableUpdates: OTAUpdateItem[];
  completedUpdates: OTAUpdateItem[]; // Updates that are already installed (from API)
  currentDownload: OTAProgress | null;
  otaBlockingOverlay: boolean;
  hasNewUpdates: boolean;
  currentContentVersion: number | null;

  setAvailableUpdates: (updates: OTAUpdateItem[]) => void;
  setCompletedUpdates: (updates: OTAUpdateItem[]) => void;
  updateDownloadProgress: (progress: OTAProgress) => void;
  setBlockingOverlay: (show: boolean) => void;
  setHasNewUpdates: (hasUpdates: boolean) => void;
  markUpdatesAsRead: () => void;
  clearCurrentDownload: () => void;
  setCurrentContentVersion: (version: number) => void;
}

export const createOTASlice: StateCreator<OTASlice> = (set) => ({
  availableUpdates: [],
  completedUpdates: [],
  currentDownload: null,
  otaBlockingOverlay: false,
  hasNewUpdates: false,
  currentContentVersion: null,

  setAvailableUpdates: (updates) => set({ availableUpdates: updates }),

  setCompletedUpdates: (updates) => set({ completedUpdates: updates }),

  updateDownloadProgress: (progress) =>
    set((state) => ({
      currentDownload: {
        ...progress,
        // Preserve diskSpace from previous progress if new progress doesn't have it
        diskSpace: progress.diskSpace ?? state.currentDownload?.diskSpace,
      },
    })),

  setBlockingOverlay: (show) => set({ otaBlockingOverlay: show }),

  setHasNewUpdates: (hasUpdates) => set({ hasNewUpdates: hasUpdates }),

  markUpdatesAsRead: () => set({ hasNewUpdates: false }),

  clearCurrentDownload: () => set({ currentDownload: null }),

  setCurrentContentVersion: (version) => set({ currentContentVersion: version }),
});
